import { quoteApi, quoteSupplierItemApi } from "@/main/api";
import { QuoteSupplierItemSaveCartRequest } from "@/main/models/request";
import { QuoteSelectSuppliersDataResponseItems } from "@/main/models/response";
import { chunkArray } from "@/main/services/utilsArray";

import { IPartOffer } from "../types";
import { applySavedCartGuids } from "../utilsSummary";

const CONCURRENCY = 4;

const EMPTY_SUPPLIER_OFFERS: QuoteSelectSuppliersDataResponseItems = {
	enableFindMoreSuppliers: false,
	foundSuppliersQuantity: 0,
	minimalSuppliersQuantityForOrder: 0,
	selectSupplierItems: [],
	round: 0,
};

export async function fetchSupplierOffers(
	guidQuote: string,
	guidPart: string,
	findMoreSuppliers: boolean,
): Promise<QuoteSelectSuppliersDataResponseItems> {
	const { data } = await quoteApi.getSelectSuppliers({
		quoteGuid: guidQuote,
		partGuid: guidPart,
		findMoreSuppliers,
		automaticQuote: false,
		isCompareSupplier: true,
	});

	return data.result ?? EMPTY_SUPPLIER_OFFERS;
}

export function notifySuppliersZeroInventory(guidQuote: string) {
	quoteApi.getSendSuppliersZeroInventoryEmail({ quoteGuid: guidQuote }).catch(() => {});
}

function requestOffersByPart(
	guidQuote: string,
	guidPart: string,
): Promise<QuoteSelectSuppliersDataResponseItems | null> {
	return fetchSupplierOffers(guidQuote, guidPart, true).catch(() => null);
}

export async function requestOffersFromAllSuppliers(
	guidQuote: string,
	guidParts: string[],
): Promise<QuoteSelectSuppliersDataResponseItems[]> {
	const batches: string[][] = chunkArray([...new Set(guidParts)], CONCURRENCY);

	const responses = await batches.reduce<Promise<(QuoteSelectSuppliersDataResponseItems | null)[]>>(
		async (previous, batch) => {
			const accumulated = await previous;
			const results = await Promise.all(batch.map((guidPart) => requestOffersByPart(guidQuote, guidPart)));
			return [...accumulated, ...results];
		},
		Promise.resolve([]),
	);

	const answeredResponses = responses.filter(
		(response): response is QuoteSelectSuppliersDataResponseItems => response !== null,
	);

	if (answeredResponses.length) notifySuppliersZeroInventory(guidQuote);

	return answeredResponses;
}

export async function saveSupplierOnCart(guidQuote: string, offer: IPartOffer): Promise<IPartOffer | null> {
	const request: QuoteSupplierItemSaveCartRequest = {
		guid: offer.guidQuoteSupplierItem,
		guidPart: offer.guidPart,
		guidQuote,
		guidSupplier: offer.guidSupplier,
		idPartType: offer.idPartType,
		guidSupplierInventoryWarehouse: offer.guidSupplierInventoryWarehouse,
		deliveryBySupplier: offer.deliveryBySupplier,
		quantity: 1,
	};

	const { data } = await quoteSupplierItemApi.saveCart(request);

	if (!data.result) return null;

	return applySavedCartGuids(offer, data.result);
}

export async function clearCart(guidQuote: string): Promise<void> {
	await quoteSupplierItemApi.clearAllCart(guidQuote);
}
